import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useUser, useClerk } from '@clerk/clerk-react';
import { localAuthEnabled, localLogout, localEmailAtual } from '../lib/localAuth';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Tooltip } from './Tooltip';
import { TOOLTIPS, type TooltipKey } from '../lib/tooltips';
import { SupportWidget } from './suporte/SupportWidget';
import { FrescorBanner } from './FrescorBanner';

/**
 * Sidebar ÚNICA da plataforma, condicional por papel:
 *  - CLIENTE (incorporadora): vê SÓ a navegação do cliente.
 *  - SUPER-ADMIN (operador): vê a navegação do cliente MAIS um bloco separado
 *    "Console" com os módulos internos.
 * A visibilidade do Console é puramente visual - o backend continua protegido
 * por superAdminProcedure. Um cliente comum nunca vê nem alcança o Console.
 */

// Navegação do CLIENTE (incorporadora). `tip` aponta a orientação (tooltip);
// `icon` é o ícone da função (renderizado antes do rótulo, ver IconeNav).
type NavItemDef = { href: string; label: string; icon: IconeNavKey; tip?: TooltipKey };

// Núcleo da área do cliente.
const CLIENTE_CORE: NavItemDef[] = [
  { href: '/importar', label: 'Importar', icon: 'importar', tip: 'navImportar' },
  { href: '/resultado', label: 'Resultado', icon: 'resultado', tip: 'navResultado' },
  { href: '/risco', label: 'Contratos em risco', icon: 'risco', tip: 'navRisco' },
  { href: '/comunicacoes', label: 'Comunicações', icon: 'comunicacoes', tip: 'navComunicacoes' },
  { href: '/ajustes', label: 'Ajustes', icon: 'ajustes', tip: 'navAjustes' },
  // Índice INCC é CONSULTA da incorporadora (o índice que reajusta seus
  // contratos) — pertence à área do cliente, não ao Dashboard do super-admin.
  { href: '/indice-incc', label: 'Índice INCC', icon: 'indices' },
  { href: '/tutoriais', label: 'Tutoriais', icon: 'tutoriais' },
];

// Seção "App do Cliente" (separada por uma linha, como o Dashboard).
const CLIENTE_APP: NavItemDef[] = [
  { href: '/app-cliente', label: 'App do Cliente', icon: 'appcliente', tip: 'navAppCliente' },
  { href: '/app-cliente/obras', label: 'Obras', icon: 'obras' },
  { href: '/app-cliente/boletos', label: 'Boletos', icon: 'boletos' },
];

// Bloco DASHBOARD (só super-admin).
const CONSOLE_NAV: { href: string; label: string; icon: IconeNavKey }[] = [
  { href: '/admin/visao-geral', label: 'Visão geral', icon: 'visaogeral' },
  { href: '/admin/clientes', label: 'Clientes', icon: 'clientes' },
  { href: '/admin/financeiro', label: 'Financeiro', icon: 'financeiro' },
  { href: '/admin/leads', label: 'Leads / CRM', icon: 'leads' },
  { href: '/admin/suporte', label: 'Suporte com IA', icon: 'suporte' },
  { href: '/admin/saude', label: 'Saúde dos agentes', icon: 'saude' },
];

type IconeNavKey =
  | 'importar' | 'resultado' | 'risco' | 'comunicacoes' | 'appcliente' | 'obras' | 'boletos' | 'ajustes' | 'tutoriais'
  | 'visaogeral' | 'clientes' | 'financeiro' | 'leads' | 'suporte' | 'saude' | 'indices';

/** Ícone da função de cada aba do menu. Herda a cor do texto (ativo/inativo). */
function IconeNav({ nome }: { nome: IconeNavKey }) {
  const s = { stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };
  const svg = (children: React.ReactNode) => (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="shrink-0">{children}</svg>
  );
  switch (nome) {
    case 'importar': return svg(<><path d="M12 15V3m0 0L8 7m4-4l4 4" {...s} /><path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" {...s} /></>);
    case 'resultado': return svg(<><path d="M4 20V11M10 20V4M16 20v-6" {...s} /><path d="M2 20h20" {...s} /></>);
    case 'risco': return svg(<><path d="M12 3l9 16H3L12 3z" {...s} /><path d="M12 10v4M12 17h.01" {...s} /></>);
    case 'comunicacoes': return svg(<path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H9l-4 3V6z" {...s} />);
    case 'appcliente': return svg(<><rect x="7" y="3" width="10" height="18" rx="2.5" {...s} /><path d="M11 18h2" {...s} /></>);
    case 'obras': return svg(<><path d="M5 21V8l7-5 7 5v13" {...s} /><path d="M9 21v-6h6v6" {...s} /></>);
    case 'boletos': return svg(<><path d="M6 3h8l5 5v12a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" {...s} /><path d="M14 3v5h5M9 13h6M9 16h6" {...s} /></>);
    case 'ajustes': return svg(<><circle cx="12" cy="12" r="3.2" {...s} /><path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" {...s} /></>);
    case 'tutoriais': return svg(<><path d="M4 5.5A1.5 1.5 0 015.5 4H11v14H5.5A1.5 1.5 0 014 16.5v-11z" {...s} /><path d="M20 5.5A1.5 1.5 0 0018.5 4H13v14h5.5a1.5 1.5 0 001.5-1.5v-11z" {...s} /></>);
    case 'visaogeral': return svg(<><rect x="3" y="3" width="7" height="7" rx="1.5" {...s} /><rect x="14" y="3" width="7" height="7" rx="1.5" {...s} /><rect x="3" y="14" width="7" height="7" rx="1.5" {...s} /><rect x="14" y="14" width="7" height="7" rx="1.5" {...s} /></>);
    case 'clientes': return svg(<><circle cx="9" cy="8" r="3" {...s} /><path d="M3.5 20a5.5 5.5 0 0111 0" {...s} /><path d="M16 5.3a3 3 0 010 5.4M20.5 20a5.5 5.5 0 00-3.5-5.1" {...s} /></>);
    case 'financeiro': return svg(<><circle cx="12" cy="12" r="9" {...s} /><path d="M12 6.5v11M14.5 9a2.3 2.3 0 00-2.2-1.5h-.6a2 2 0 000 4h1.2a2 2 0 010 4h-.6A2.3 2.3 0 019.3 18" {...s} /></>);
    case 'leads': return svg(<path d="M3 5h18l-7 8v6l-4-2v-4L3 5z" {...s} />);
    case 'suporte': return svg(<><path d="M4 13a8 8 0 0116 0" {...s} /><rect x="3" y="13" width="4" height="6" rx="1.5" {...s} /><rect x="17" y="13" width="4" height="6" rx="1.5" {...s} /></>);
    case 'saude': return svg(<path d="M3 12h4l2-5 4 11 2-6h6" {...s} />);
    case 'indices': return svg(<><path d="M3 17l6-6 4 4 8-8" {...s} /><path d="M17 7h4v4" {...s} /></>);
  }
}

function LogoMark() {
  return <img src="/logo-ama-ink.png" alt="AMA Capital" className="block h-9 w-auto" />;
}

function iniciais(nome?: string | null, email?: string | null): string {
  const base = (nome || email || 'você').trim();
  const partes = base.split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0]![0]! + partes[1]![0]!).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

/**
 * Redimensiona a imagem num quadrado `size`px preservando a proporção (CONTAIN,
 * não cover) — a logo INTEIRA cabe, centrada, com transparência em volta. Antes
 * era cover, que RECORTAVA a logo num quadrado (e depois virava um círculo na
 * sidebar / um quadrado no app do comprador). Agora a imagem aparece completa
 * em qualquer lugar. Canvas quadrado ≥192 mantém a logo válida como ícone de PWA.
 */
function redimensionarParaDataUrl(file: File, size = 256): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas indisponível'));
        return;
      }
      const escala = Math.min(size / img.width, size / img.height); // CONTAIN
      const w = img.width * escala;
      const h = img.height * escala;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/webp', 0.9));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('imagem inválida'));
    };
    img.src = url;
  });
}

/**
 * Marca da incorporadora, sempre no TOPO da sidebar (logo abaixo do
 * logotipo AMA Capital) — inclusive no Console do super-admin, não só na
 * área do cliente. Logo circular (clique para enviar/trocar a imagem) e o
 * nome abaixo, editável in-place (clique no nome abre um campo de texto —
 * salva ao confirmar).
 *
 * ⚠️ Numa conta NOVA, o registro da incorporadora é semeado em background
 * (nome = e-mail) por um efeito em App.tsx, de forma assíncrona — corre em
 * paralelo com esta primeira consulta, então pode ainda não existir no
 * instante exato em que ela roda (`data` vem `null`). Como `useAsync` não
 * refaz a consulta sozinho, sem esta retentativa a marca sumiria pra sempre
 * na sessão (só reapareceria num F5). As poucas tentativas curtas cobrem essa
 * janela sem virar polling permanente.
 */
function IncorporadoraBrand() {
  const { data, reload } = useAsync(() => trpc.incorporadora.query());
  const inputRef = useRef<HTMLInputElement>(null);
  const nomeInputRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [editandoNome, setEditandoNome] = useState(false);
  const [nomeEdit, setNomeEdit] = useState('');
  // Espelho local do logo: reflete a troca/remoção NA HORA, sem depender do
  // reload da query (que pode voltar em cache e "não subir" a nova imagem).
  // `undefined` = ainda usa o valor do servidor; `string|null` = valor local.
  const [logoLocal, setLogoLocal] = useState<string | null | undefined>(undefined);
  const [erroLogo, setErroLogo] = useState<string | null>(null);
  const tentativas = useRef(0);

  useEffect(() => {
    if (data || tentativas.current >= 6) return;
    const t = setTimeout(() => {
      tentativas.current += 1;
      reload();
    }, 1000);
    return () => clearTimeout(t);
  }, [data, reload]);

  if (!data) return null;
  const logo = logoLocal !== undefined ? logoLocal : data.logoUrl ?? null;

  async function onArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reenviar o mesmo arquivo
    if (!file) return;
    setErroLogo(null);
    setEnviando(true);
    try {
      const logoUrl = await redimensionarParaDataUrl(file);
      await trpc.salvarLogoIncorporadora.mutate({ logoUrl });
      setLogoLocal(logoUrl); // reflete a nova imagem imediatamente
      reload();
    } catch (err) {
      // Antes o erro era engolido em silêncio (parecia que "não subia"). Agora
      // avisa: imagem ilegível (HEIC do iPhone, corrompida) ou recusada.
      const m = err instanceof Error ? err.message : '';
      setErroLogo(
        /inválid|invalid|logo/i.test(m)
          ? 'Não consegui usar essa imagem. Envie um PNG, JPG ou WebP.'
          : 'Não foi possível enviar a imagem. Tente novamente.',
      );
    } finally {
      setEnviando(false);
    }
  }

  function iniciarEdicaoNome() {
    setNomeEdit(data!.nome);
    setEditandoNome(true);
  }

  async function salvarNome() {
    setEditandoNome(false);
    const nome = nomeEdit.trim();
    if (!nome || nome === data!.nome) return;
    try {
      await trpc.syncIncorporadora.mutate({ nome });
      reload();
    } catch {
      /* silencioso: mantém o nome atual */
    }
  }

  async function removerLogo() {
    setErroLogo(null);
    setEnviando(true);
    try {
      await trpc.salvarLogoIncorporadora.mutate({ logoUrl: null });
      setLogoLocal(null); // volta pro placeholder imediatamente
      reload();
    } catch {
      setErroLogo('Não foi possível remover a imagem.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    // Bloco da incorporadora CENTRALIZADO (logo, nome e ações centrados).
    <div className="flex flex-col items-center px-3.5 pb-5 pt-6 text-center" data-testid="sidebar-incorporadora">
      {/* Logo com as ações NO PRÓPRIO elemento: clicar troca (overlay de câmera
          no hover deixa claro); o "×" no canto remove. Nada mais escondido
          embaixo do nome. */}
      <div className="relative">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={enviando}
          title={logo ? 'Trocar o logo da incorporadora' : 'Enviar o logo da incorporadora'}
          aria-label={logo ? 'Trocar o logo da incorporadora' : 'Enviar o logo da incorporadora'}
          // rounded-2xl + object-contain: mostra a logo INTEIRA (não recorta num
          // círculo). Fundo neutro só enquadra; a imagem aparece completa.
          className="group relative h-16 w-16 overflow-hidden rounded-2xl border border-line disabled:opacity-60"
          style={{ background: 'var(--surface-2)' }}
          data-testid="logo-trocar"
        >
          {logo ? (
            <img src={logo} alt={data.nome} className="h-full w-full object-contain" data-testid="incorporadora-logo" />
          ) : (
            <span
              className="flex h-full w-full items-center justify-center text-lg font-semibold"
              style={{ color: 'var(--ink-2)' }}
              data-testid="incorporadora-logo-placeholder"
            >
              {iniciais(data.nome)}
            </span>
          )}
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100">
            {enviando ? (
              <span className="text-[10px] font-medium">enviando…</span>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                  <path d="M4 8.5A1.5 1.5 0 015.5 7H8l1-1.5h6L16 7h2.5A1.5 1.5 0 0120 8.5v9A1.5 1.5 0 0118.5 19h-13A1.5 1.5 0 014 17.5v-9z" strokeLinejoin="round" />
                  <circle cx="12" cy="12.5" r="3" />
                </svg>
                <span className="text-[9px] font-semibold uppercase tracking-wide">{logo ? 'Trocar' : 'Enviar'}</span>
              </>
            )}
          </span>
        </button>
        {logo && !enviando && (
          <button
            type="button"
            onClick={removerLogo}
            title="Remover o logo"
            aria-label="Remover o logo"
            data-testid="logo-remover"
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-line bg-surface text-ink-3 shadow-sm transition-colors hover:border-risk hover:text-risk"
          >
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        )}
        {/* Badge de câmera SEMPRE visível: deixa óbvio que dá para enviar/trocar. */}
        {!enviando && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            title={logo ? 'Trocar imagem' : 'Enviar imagem'}
            aria-label={logo ? 'Trocar o logo' : 'Enviar o logo'}
            className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-line bg-surface text-ink-3 shadow-sm transition-colors hover:border-accent hover:text-accent"
          >
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
              <path d="M4 8.5A1.5 1.5 0 015.5 7H8l1-1.5h6L16 7h2.5A1.5 1.5 0 0120 8.5v9A1.5 1.5 0 0118.5 19h-13A1.5 1.5 0 014 17.5v-9z" strokeLinejoin="round" />
              <circle cx="12" cy="12.5" r="2.6" />
            </svg>
          </button>
        )}
      </div>

      {editandoNome ? (
        <input
          ref={nomeInputRef}
          autoFocus
          value={nomeEdit}
          onChange={(e) => setNomeEdit(e.target.value)}
          onBlur={salvarNome}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setEditandoNome(false);
          }}
          maxLength={160}
          className="mt-3 w-full max-w-[190px] rounded-md border border-accent bg-surface px-2 py-1.5 text-left text-[15px] font-semibold text-ink outline-none"
          data-testid="incorporadora-nome-input"
        />
      ) : (
        <button
          type="button"
          onClick={iniciarEdicaoNome}
          title="Editar o nome da incorporadora"
          aria-label="Editar o nome da incorporadora"
          className="group/nome mt-3 flex max-w-full items-center gap-1.5 rounded-md py-1 pr-2 text-left outline-none hover:bg-bg-warm focus-visible:ring-2 focus-visible:ring-accent"
          data-testid="incorporadora-nome"
        >
          <span className="max-w-[160px] truncate text-[15px] font-semibold text-ink">{data.nome}</span>
          <svg
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            aria-hidden="true"
            className="shrink-0 text-ink-4 transition-colors group-hover/nome:text-accent"
          >
            <path
              d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {erroLogo && (
        <p className="mt-1.5 max-w-[190px] text-[11px] leading-snug" style={{ color: 'var(--risk)' }} data-testid="logo-erro">
          {erroLogo}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        data-testid="incorporadora-logo-input"
        onChange={onArquivo}
      />
    </div>
  );
}

/** Base da sidebar (apresentacional): avatar + nome/e-mail + "Sair". */
function ContaView({
  nome,
  email,
  onSair,
  topo,
}: {
  nome: string;
  email: string;
  onSair: () => void;
  topo?: React.ReactNode;
}) {
  return (
    <div className="mt-auto border-t border-line px-3 py-3" data-testid="conta-rodape">
      {topo}
      <div className="flex items-center gap-2.5 px-2 py-1.5">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
        >
          {iniciais(nome, email)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink">{nome}</div>
          <div className="truncate text-xs text-ink-4">{email}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onSair}
        className="mt-1 flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-ink-3 transition-colors hover:bg-bg-warm hover:text-ink"
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
          <path
            d="M15 12H4m0 0l3.5-3.5M4 12l3.5 3.5M14 5h4a2 2 0 012 2v10a2 2 0 01-2 2h-4"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Sair
      </button>
    </div>
  );
}

/**
 * Conta via Clerk (só quando NÃO está em modo local). Sem seletor de
 * organização: a incorporadora é 100% automática e invisível (1:1 com a
 * conta), inclusive para o operador (super-admin) — nunca há tela de
 * criar/selecionar.
 */
function ContaClerk() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const nome = user?.fullName || (email ? email.split('@')[0]! : 'Você');
  // Sair leva ao LOGIN (/entrar), NUNCA à landing de vendas — mesmo que a rota
  // atual seja "/". redirectUrl garante que o Clerk navegue após encerrar.
  return <ContaView nome={nome} email={email} onSair={() => void signOut({ redirectUrl: '/entrar' })} />;
}

/** Conta no modo local (SEM Clerk). */
function ContaLocal() {
  const email = localEmailAtual() ?? '';
  const nome = email ? email.split('@')[0]! : 'Você';
  return (
    <ContaView
      nome={nome}
      email={email}
      onSair={() => {
        localLogout();
        window.location.href = '/entrar'; // login, nunca a landing de vendas
      }}
    />
  );
}

function ItemNav({
  href,
  label,
  icon,
  active,
  tip,
  onNavigate,
}: {
  href: string;
  label: string;
  icon?: IconeNavKey;
  active: boolean;
  tip?: TooltipKey;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <Link
        href={href}
        onClick={onNavigate}
        className={`flex h-11 flex-1 items-center gap-2.5 rounded-[11px] px-3 text-sm transition-colors md:h-[42px] ${
          active ? 'bg-accent-soft font-semibold text-accent' : 'font-medium text-ink-2 hover:bg-bg-warm'
        }`}
      >
        {/* Ícone SEMPRE na cor de destaque (accent), diferente da cor do texto —
            faz cada aba se destacar (o texto inativo é neutro, o ícone é verde). */}
        {icon && (
          <span className="text-accent" aria-hidden="true">
            <IconeNav nome={icon} />
          </span>
        )}
        <span className="truncate">{label}</span>
      </Link>
      {tip && <Tooltip text={TOOLTIPS[tip]} placement="right" label={`O que é ${label}`} className="mr-1" />}
    </div>
  );
}

function IconeHamburguer() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconeFechar() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function Shell({
  children,
  superAdmin = false,
}: {
  children: React.ReactNode;
  superAdmin?: boolean;
}) {
  const [location] = useLocation();
  const [drawerAberto, setDrawerAberto] = useState(false);
  const hamburguerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const isActive = (href: string) => location === href || location.startsWith(href + '/');
  const noConsole = location.startsWith('/admin');
  const fecharDrawer = () => setDrawerAberto(false);

  // Fecha com Esc e move o foco para dentro do drawer ao abrir (volta ao
  // botão de hambúrguer ao fechar) - navegação por teclado, drawer é um diálogo.
  useEffect(() => {
    if (!drawerAberto) return;
    drawerRef.current?.querySelector<HTMLElement>('button, a')?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setDrawerAberto(false);
        hamburguerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerAberto]);

  return (
    // h-dvh (altura EXATA, não só mínima) — necessário pra cascata de
    // min-h-0 abaixo funcionar (ver comentário na coluna de conteúdo).
    // `dvh` (dynamic viewport height, não `vh`) evita o corte/salto clássico
    // de `100vh` no mobile quando a barra de endereço do navegador
    // aparece/some durante o scroll.
    <div className="flex h-dvh bg-bg-warm text-ink">
      {/* Overlay do drawer - só mobile, só quando aberto. Toque fora fecha. */}
      {drawerAberto && (
        <div
          data-testid="drawer-overlay"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={fecharDrawer}
          aria-hidden="true"
        />
      )}

      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal={drawerAberto}
        aria-label="Menu de navegação"
        data-testid="drawer-panel"
        data-aberto={drawerAberto}
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] -translate-x-full flex-col overflow-y-auto border-r border-line bg-surface transition-transform duration-200 ease-out motion-reduce:transition-none md:static md:z-auto md:w-64 md:max-w-none md:translate-x-0 ${
          drawerAberto ? 'translate-x-0' : ''
        }`}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-6">
          <LogoMark />
          <button
            type="button"
            onClick={fecharDrawer}
            aria-label="Fechar menu"
            data-testid="drawer-fechar"
            className="flex h-11 w-11 items-center justify-center rounded-[10px] text-ink-3 hover:bg-bg-warm hover:text-ink md:hidden"
          >
            <IconeFechar />
          </button>
        </div>

        {/* Marca da incorporadora (logo + nome) sempre no TOPO, acima de tudo
            - inclusive no Console do admin. */}
        <IncorporadoraBrand />

        {/* Bloco DASHBOARD - SÓ super-admin */}
        {superAdmin && (
          <div className="border-t border-line px-3.5 py-4">
            <div className="mb-2 flex items-center gap-2 px-1">
              <span
                className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.11em] text-accent"
                style={{ background: 'var(--accent-soft)' }}
              >
                Dashboard
              </span>
            </div>
            <nav className="flex flex-col gap-1">
              {CONSOLE_NAV.map((item) => (
                <ItemNav
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  icon={item.icon}
                  active={isActive(item.href)}
                  onNavigate={fecharDrawer}
                />
              ))}
            </nav>
          </div>
        )}

        {/* Navegação do CLIENTE - sempre visível (abaixo do Dashboard p/ super-admin) */}
        <nav className={`flex flex-col gap-1 px-3.5 pb-4 ${superAdmin ? 'border-t border-line pt-4' : 'pt-0'}`}>
          {superAdmin && (
            <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.11em] text-ink-4">
              Área do cliente
            </div>
          )}
          {CLIENTE_CORE.map((item) => (
            <ItemNav
              key={item.href}
              href={item.href}
              label={item.label}
              icon={item.icon}
              active={location === item.href || isActive(item.href)}
              tip={item.tip}
              onNavigate={fecharDrawer}
            />
          ))}
        </nav>

        {/* Seção APP DO CLIENTE - separada por uma LINHA (como o Dashboard). */}
        <nav className="flex flex-col gap-1 border-t border-line px-3.5 pb-4 pt-4">
          <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.11em] text-ink-4">
            App do Cliente
          </div>
          {CLIENTE_APP.map((item) => (
            <ItemNav
              key={item.href}
              href={item.href}
              label={item.label}
              icon={item.icon}
              active={location === item.href}
              tip={item.tip}
              onNavigate={fecharDrawer}
            />
          ))}
        </nav>

        {localAuthEnabled() ? <ContaLocal /> : <ContaClerk />}
      </aside>

      {/* min-h-0 é essencial aqui: sem ele, um flex item com filho
          `overflow-y-auto` (o <main> abaixo) IGNORA o overflow e cresce pra
          caber todo o conteúdo em vez de conter o próprio scroll (pegadinha
          clássica do Flexbox — `min-height` de um flex item é `auto` por
          padrão, não `0`). Sem isso, telas longas (ex.: /risco com muitos
          contratos) faziam o <main> esticar além da viewport e o SCROLL DO
          DOCUMENTO ficar travado/errático no mobile (causa raiz real do
          header "sumindo"/deslocando ao rolar - não bastava só trocar
          sticky→fixed). */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* No mobile é `fixed` DE VERDADE (não `sticky`) — sticky depende do
            ancestral com scroll mais próximo, e aqui o <header> é IRMÃO do
            <main overflow-auto> (não pai), não do documento; combinado com
            containers flex/transform vizinhos (o <aside> do drawer usa
            transform pra deslizar), sticky é uma fonte clássica de bugs
            sutis em iOS Safari (o header "sumindo"/deslocando ao rolar).
            `fixed` ancora direto na viewport, sem essa ambiguidade — daí o
            spacer logo abaixo, do mesmo tamanho, pra compensar o espaço que
            sai do fluxo normal do documento. */}
        <header className="fixed inset-x-0 top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-bg-warm px-4 md:static md:h-auto md:justify-between md:px-6 md:py-3">
          <button
            ref={hamburguerRef}
            type="button"
            onClick={() => setDrawerAberto(true)}
            aria-label="Abrir menu"
            data-testid="drawer-abrir"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] text-ink-2 hover:bg-surface md:hidden"
          >
            <IconeHamburguer />
          </button>
          <img src="/logo-ama-ink.png" alt="AMA Capital" className="h-7 w-auto md:hidden" />
          <div className="hidden text-sm text-ink-3 md:block">
            {noConsole ? 'Dashboard' : 'Retenção pós-venda'}
          </div>
        </header>
        {/* Spacer: mesma altura do header fixo (h-14), só no mobile — evita
            que o conteúdo comece escondido embaixo dele. */}
        <div className="h-14 shrink-0 md:hidden" aria-hidden="true" />
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6">
          {/* Aviso de frescor da carteira — só na área do cliente (nunca no console). */}
          {!noConsole && <FrescorBanner />}
          {children}
        </main>
      </div>

      {/* Widget de suporte com IA - SÓ na área do cliente (nunca no console). */}
      {!noConsole && <SupportWidget />}
    </div>
  );
}
